// Logical "day" for habit/recurring completions and streaks.
//
// Users can set a day-start hour (default 3am local) so that something completed
// late at night — e.g. a nightly prayer finished at 1am — counts toward the day
// that just ended rather than the new calendar day. We derive the logical day by
// shifting a timestamp back by the timezone offset *and* the day-start hour, then
// taking the calendar date.

export const DEFAULT_DAY_START_HOUR = 3

/**
 * Local YYYY-MM-DD for a timestamp, honoring the user's day-start hour.
 * @param ms epoch millis
 * @param tzOffsetMin minutes, as from Date.getTimezoneOffset() (UTC − local)
 * @param dayStartHour hour 0–23 the day rolls over at (default 3am)
 */
export function logicalDayStr(
  ms: number,
  tzOffsetMin: number,
  dayStartHour: number = DEFAULT_DAY_START_HOUR,
): string {
  return new Date(ms - tzOffsetMin * 60_000 - dayStartHour * 3_600_000).toISOString().slice(0, 10)
}

const DAY_MS = 86_400_000

/** Absolute epoch-ms at which the logical day `dayKey` (YYYY-MM-DD) begins. */
export function logicalDayStartMs(
  dayKey: string,
  tzOffsetMin: number,
  dayStartHour: number = DEFAULT_DAY_START_HOUR,
): number {
  return Date.parse(`${dayKey}T00:00:00Z`) + tzOffsetMin * 60_000 + dayStartHour * 3_600_000
}

/**
 * Consecutive-day streak counting back from `anchorMs`'s logical day, over a
 * 90-day window. `days` is the set of logical-day keys with activity (completions
 * + frozen dates). When `grace` is true (the default) and the anchor day itself
 * has no activity, counting starts from the previous day — so "today not done
 * yet" doesn't zero the streak. Set `grace` false to count strictly from the
 * anchor day (used for the nightly milestone check anchored on yesterday).
 * `isDue` (per-habit streaks) marks unscheduled days, which are skipped: they
 * neither break the streak nor count unless there was activity on them.
 */
export function streakFromDays(
  days: Set<string>,
  anchorMs: number,
  tzOffsetMin: number,
  dayStartHour: number,
  grace = true,
  isDue?: (dayKey: string) => boolean,
): number {
  const anchorKey = logicalDayStr(anchorMs, tzOffsetMin, dayStartHour)
  const startOffset = days.has(anchorKey) ? 0 : grace ? 1 : 0
  let streak = 0
  for (let i = startOffset; i < 90; i++) {
    const key = logicalDayStr(anchorMs - i * DAY_MS, tzOffsetMin, dayStartHour)
    if (days.has(key)) streak++
    else if (isDue && !isDue(key)) continue
    else break
  }
  return streak
}

// "Take a breather" (away/pause) window helpers. Windows are inclusive logical-
// day keys (YYYY-MM-DD); `until` omitted means indefinite.

/** Whether `todayKey` falls within an active breather window. */
export function isOnBreather(
  from: string | undefined | null,
  until: string | undefined | null,
  todayKey: string,
): boolean {
  if (!from) return false
  if (todayKey < from) return false
  if (!until) return true
  return todayKey <= until
}

/**
 * Logical-day keys covered by a breather window, clamped to [from, upTo] and to
 * the last `maxDays` days so streak math stays bounded. Treating these as frozen
 * days bridges the streak and lets the dot-grid mark them neutral.
 */
export function breatherDayKeys(
  from: string | undefined | null,
  until: string | undefined | null,
  upToKey: string,
  maxDays = 120,
): string[] {
  if (!from) return []
  const end = until && until < upToKey ? until : upToKey
  if (end < from) return []
  const fromMs = Date.parse(`${from}T00:00:00Z`)
  const keys: string[] = []
  let ms = Date.parse(`${end}T00:00:00Z`)
  for (let i = 0; i < maxDays && ms >= fromMs; i++) {
    keys.push(new Date(ms).toISOString().slice(0, 10))
    ms -= DAY_MS
  }
  return keys
}

/** Convenience: is the user on a breather right now, from their prefs. */
export function isOnBreatherNow(prefs: {
  breatherFrom?: string
  breatherUntil?: string
  tzOffsetMinutes?: number
  dayStartHour?: number
}): boolean {
  const tz = prefs.tzOffsetMinutes ?? 0
  const ds = prefs.dayStartHour ?? DEFAULT_DAY_START_HOUR
  return isOnBreather(prefs.breatherFrom, prefs.breatherUntil, logicalDayStr(Date.now(), tz, ds))
}

/** Frozen-date keys plus the breather window — bridges streaks over a breather. */
export function frozenWithBreather(
  frozenDates: string[] | undefined,
  from: string | undefined | null,
  until: string | undefined | null,
  anchorMs: number,
  tzOffsetMin: number,
  dayStartHour: number = DEFAULT_DAY_START_HOUR,
): string[] {
  return [
    ...(frozenDates ?? []),
    ...breatherDayKeys(from, until, logicalDayStr(anchorMs, tzOffsetMin, dayStartHour)),
  ]
}

/** Streak from raw completion timestamps + frozen-date keys. */
export function computeStreak(
  completedAtMs: number[],
  frozenDates: string[],
  anchorMs: number,
  tzOffsetMin: number,
  dayStartHour: number = DEFAULT_DAY_START_HOUR,
  grace = true,
  isDue?: (dayKey: string) => boolean,
): number {
  const days = new Set<string>([
    ...completedAtMs.map((ms) => logicalDayStr(ms, tzOffsetMin, dayStartHour)),
    ...frozenDates,
  ])
  return streakFromDays(days, anchorMs, tzOffsetMin, dayStartHour, grace, isDue)
}
